import type { BatteryRebateOutput, PaybackOutput } from "./types.js";

// Plain-English result copy for solar-battery (federal battery rebate + payback).
// Lives in the rules package so every surface renders identical wording from one
// source. Regulatory shape (binding): this is INFORMATION, not advice.
//   - No "save $X", no "buy a battery", no "you should size X kWh", no product
//     or installer recommendation. We state what the federal discount IS and how
//     it is derived from the CER/SRES schedule.
//   - The dollar rebate is ALWAYS framed "indicative, based on an STC price of
//     $X" — the STC price floats, so it is never a locked quote.
//   - Payback is cited arithmetic with its assumptions surfaced, presented as a
//     RANGE — never a precise "you'll save $X/yr" promise.
//   - Every output cites CER / the Cheaper Home Batteries program. The
//     installer-affiliate handoff is NOT mentioned here (facts only).

export const NOT_ADVICE_DISCLAIMER =
  "Educational tool. Not financial advice. The federal battery discount is delivered as Small-scale Technology Certificates (STCs); the STC price floats, so dollar figures are indicative, not a quote. Eligibility and the final discount are determined by your accredited installer and the Clean Energy Regulator (CER).";

/** Whole-dollar AUD with thousands separators, rendered the same everywhere. */
export function formatAud(amount: number): string {
  return `A$${Math.round(amount).toLocaleString("en-AU")}`;
}

/** Headline for the rebate — the entitlement number, never a "saving". */
export function rebateHeadline(out: BatteryRebateOutput): string {
  if (!out.rebateAvailable) {
    return "No federal battery rebate applies on these details";
  }
  return `Indicative federal battery discount: about ${formatAud(out.indicativeRebateAud)} (${out.stcCount} STCs at an STC price of ${formatAud(out.stcPriceUsedAud)})`;
}

/** Why no/partial rebate — descriptive, not a nudge. */
export function eligibilityBullet(out: BatteryRebateOutput): string | null {
  switch (out.ineligibleReason) {
    case "below-min-usable":
      return `A usable capacity of ${out.enteredUsableKwh} kWh is below the program's minimum, so no federal discount applies.`;
    case "over-nominal-cap":
      return "The battery's nominal capacity is above the program's maximum, so it is not eligible for the federal discount.";
    case "fails-core-eligibility":
      return "The federal discount requires an eligible install (a single job with new or existing rooftop solar, a CER-approved product, and on-grid systems VPP-capable). On the details given, those conditions aren't met.";
    case null:
      return null;
  }
}

/** How the rebate was derived from the deeming factor + taper. Arithmetic, not advice. */
export function rebateBullet(out: BatteryRebateOutput): string {
  if (!out.rebateAvailable) {
    return eligibilityBullet(out) ?? "No federal battery rebate applies.";
  }
  const cap = out.cappedAtDiscountedCeiling
    ? ` Usable capacity above the discounted cap earns no further STCs, so the discount was sized on ${out.eligibleUsableKwh} kWh of your ${out.enteredUsableKwh} kWh.`
    : "";
  return `The discount is delivered as ${out.stcCount} STCs — the CER deeming factor applied to your eligible usable capacity, with the federal subsidy rate stepping down across higher capacity bands.${cap} At an indicative STC price of ${formatAud(out.stcPriceUsedAud)} that is about ${formatAud(out.indicativeRebateAud)}. The STC price floats, so treat the dollar figure as indicative, not a quote.`;
}

/** Source line, composed from the schedule metadata so it names the rules in force. */
export function sourceBullet(scheduleKey: string, verifiedAt: string): string {
  return `These figures use the Cheaper Home Batteries (SRES) schedule effective ${scheduleKey} (Clean Energy Regulator), last verified ${verifiedAt}. The STC deeming factor steps down twice a year (January and July) — always check cer.gov.au.`;
}

export function rebateExplanationBullets(
  out: BatteryRebateOutput,
  verifiedAt: string,
): string[] {
  return [
    rebateBullet(out),
    sourceBullet(out.scheduleKey, verifiedAt),
    NOT_ADVICE_DISCLAIMER,
  ];
}

/* ------------------------------------------------------------------------- */
/* Payback copy — always a range + assumptions, never a single promise.      */
/* ------------------------------------------------------------------------- */

/** A range like "about 7–11 years", or an honest "doesn't pay back" line. */
export function paybackHeadline(out: PaybackOutput): string {
  if (out.neverPaysBack) {
    return "On the savings range given, the battery doesn't recover its net cost within a simple-payback view";
  }
  if (out.fastYears !== null && out.slowYears !== null) {
    return `Indicative simple payback: roughly ${out.fastYears}–${out.slowYears} years (range, not a promise)`;
  }
  // One side null (zero saving at that bound): show the side that pays back.
  const yrs = out.fastYears ?? out.slowYears;
  return `Indicative simple payback: roughly ${yrs} years or longer (range, not a promise)`;
}

export function paybackBullet(out: PaybackOutput): string {
  return `After the indicative federal rebate, the net up-front cost is ${formatAud(out.netCostAud)}. Against an annual bill saving of ${formatAud(out.annualSavingLowAud)}–${formatAud(out.annualSavingHighAud)}, that is a simple payback range — faster at the higher saving, slower at the lower.`;
}

export function paybackExplanationBullets(out: PaybackOutput): string[] {
  return [
    paybackBullet(out),
    ...out.assumptions.map((a) => a),
    NOT_ADVICE_DISCLAIMER,
  ];
}
